import { InfoCircleOutlined, LeftOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  notification,
  Row,
} from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { BASE_API_CONFIG } from "../../util/Api";
import { useSelector, useDispatch } from "react-redux";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import Upload from "../../components/Upload";
import { getLabel, isValidString } from "../../util/ApiHelper";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";

const { TextArea } = Input;

const EditPolicyDetails = () => {
  const { isMobile } = useGetBreakpoints();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [policyIconFiles, setPolicyIconFiles] = useState([{ src: "" }]);
  const [referenceDocs, setReferenceDocs] = useState([{ src: "" }]);
  
  const authUser = useSelector(({ auth }) => auth.userData);
  const token = authUser.token;

  const fetchPolicyDetailsById = async () => {
    dispatch(fetchStart());
    try {
      const response = await axios.get(
        `${BASE_API_CONFIG.baseURL}/policyUpdate/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const policyData = response.data;
      form.setFieldsValue({
        title: policyData.title,
        details: policyData.details,
        description: policyData.description,
      });

      // Set reference documents
      if (policyData.referenceDocument?.length > 0) {
        setReferenceDocs(policyData.referenceDocument.map((file, index) => ({
          src: file.src || file.downloadUrl,
          originalName: file.originalName || `document-${index}`,
          label: getLabel(index)
        })));
      }

      dispatch(fetchSuccess());
    } catch (error) {
      console.error("Error fetching policy details:", error);
      dispatch(fetchSuccess());
    }
  };

  useEffect(() => {
    fetchPolicyDetailsById();
  }, [id]);

  const handleReferenceDocUpload = (data) => {
    setReferenceDocs(prevFileList => {
      const updatedList = [...prevFileList];
      updatedList[data.index].src = data.src;
      if (updatedList.length < 4) {
        updatedList.push({ src: "" });
      }
      updatedList[data.index].originalName = data.originalName;
      return updatedList;
    });
  };

  const handleReferenceDocRemove = (data) => {
    setReferenceDocs(prevFileList => {
      const updatedList = [...prevFileList];
      updatedList[data.index].src = "";
      return updatedList;
    });
  };


  const referenceDocsUpload = () => (
    <div className="attachments">
      <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
        {referenceDocs?.length > 0 ? (
          referenceDocs.map((file, index) => (
            <Upload
              key={`ref-doc-${index}`}
              index={index}
              src={file.src}
              id={`ref-doc-${index}`}
              label={isValidString(file?.label) ? file?.label : getLabel(index)}
              originalName={file.originalName}
              onUpload={handleReferenceDocUpload}
              onRemove={handleReferenceDocRemove}
            />
          ))
        ) : (
          <div>No reference documents added.</div>
        )}
      </div>
    </div>
  );

  const onFinish = async (values) => {
    dispatch(fetchStart());
    try {
      const policyData = {
        title: values.title,
        details: values.details,
        referenceDocument: [...referenceDocs.filter((file) => file?.src)],
        description: values.description,
      };

      const response = await axios.put(
        `${BASE_API_CONFIG.baseURL}/policyUpdate/${id}`,
        policyData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        notification.success({
          message: "Success",
          description: "Policy has been updated successfully.",
        });
        history.push("/policy-updates");
      } else {
        notification.error({
          message: "Failed",
          description: "Failed to update policy.",
        });
      }
    } catch (error) {
      console.error("Error updating policy:", error);
      notification.error({
        message: "Failed",
        description: "Failed to update policy.",
      });
    } finally {
      dispatch(fetchSuccess());
    }
  };

  return (
    <Card className="myexim-detail-card">
      <div
        className="myexim-table-title"
        style={{ display: "flex", alignItems: "center", gap: "12px" }}
      >
        <div
          onClick={() => history.goBack()}
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <LeftOutlined style={{ fontSize: "24px" }} size="large" />
        </div>
        <span>Edit Policy</span>
      </div>

      <Form form={form} onFinish={onFinish} className="gx-pt-4">
        <Row gutter={16}>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label="Title"
              name="title"
              rules={[
                { required: true, message: "Please enter policy title" },
              ]}
            >
              <Input placeholder="Enter policy title" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={12}>
            <Form.Item
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label="Details"
              name="details"
              rules={[
                { required: true, message: "Please enter policy details" },
              ]}
            >
              <Input placeholder="Enter policy details" />
            </Form.Item>
          </Col>
        </Row>

        <Row className="gx-pt-5">
          <Col xs={24} sm={24} md={24}>
            <Form.Item
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label="Description"
              name="description"
              rules={[
                { required: true, message: "Please enter description" },
              ]}
            >
              <TextArea rows={10} placeholder="Enter policy description" />
            </Form.Item>
          </Col>
        </Row>

        <Row className="gx-pt-5">
          <Col span={24}>
            <div className="sub-header" style={{ marginLeft: "1px" }}>
              Reference documents
            </div>
            <Form.Item
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
              name="referenceDocument"
              rules={[
                {
                  required: false,
                  message: "Attachments are optional.",
                },
              ]}
            >
              {referenceDocsUpload()}
            </Form.Item>
            <Col>
              <div className="upload-tips gx-mt-4">
                <div className="header" style={{ display: "flex" }}>
                  <InfoCircleOutlined />
                  &nbsp; Tips
                </div>
                <div className="desc">
                  <Badge
                    status="default"
                    text="Supported file type: .pdf"
                  />
                  <br />
                  <Badge
                    status="default"
                    text="File must not more than 5MB"
                  />
                  <br />
                  <Badge
                    status="default"
                    text="Make sure you have good resolution"
                  />
                  <br />
                  <Badge
                    status="default"
                    text="Photo resolution must be more than 600x600 resolution"
                  />
                </div>
              </div>
            </Col>
          </Col>
        </Row>

        <Row className="gx-pt-5">
          <Col span={24}>
            <Form.Item className="gx-text-center">
              <div
                className="gx-mr-3"
                style={{
                  justifyContent: isMobile ? "center" : "end",
                  display: "flex",
                  flex: 1,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="default"
                  size="large"
                  style={{ width: 150 }}
                  onClick={() => history.goBack()}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  style={{ width: 150 }}
                >
                  Save
                </Button>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default EditPolicyDetails;